import { Fraction } from '@learnforge/demo-math/numeric';

export type FuncName =
  | 'sqrt'
  | 'cbrt'
  | 'log'
  | 'ln'
  | 'log2'
  | 'log10'
  | 'abs'
  | 'sin'
  | 'cos'
  | 'tan';

export type Expr =
  | { kind: 'num'; value: Fraction }
  | { kind: 'var'; name: string }
  | { kind: 'add'; children: Expr[] }
  | { kind: 'mul'; children: Expr[] }
  | { kind: 'pow'; base: Expr; exp: Expr }
  | { kind: 'func'; name: FuncName; args: Expr[] }
  | { kind: 'sigma'; index: string; from: Expr; to: Expr; body: Expr };

export const num = (n: Fraction | number): Expr => ({
  kind: 'num',
  value: typeof n === 'number' ? Fraction.from(n) : n,
});

export const variable = (name: string): Expr => ({
  kind: 'var',
  name,
});

export const add = (...c: Expr[]): Expr => ({
  kind: 'add',
  children: c,
});

export const mul = (...c: Expr[]): Expr => ({
  kind: 'mul',
  children: c,
});

export const pow = (b: Expr, e: Expr): Expr => ({
  kind: 'pow',
  base: b,
  exp: e,
});

export const func = (name: FuncName, ...args: Expr[]): Expr => ({
  kind: 'func',
  name,
  args,
});

export const sigma = (index: string, from: Expr, to: Expr, body: Expr): Expr => ({
  kind: 'sigma',
  index,
  from,
  to,
  body,
});

export function equals(a: Expr, b: Expr): boolean {
  if (a.kind !== b.kind) return false;

  switch (a.kind) {
    case 'num': {
      const bNum = b as typeof a;
      return a.value.equals(bNum.value);
    }
    case 'var': {
      const bVar = b as typeof a;
      return a.name === bVar.name;
    }
    case 'add': {
      const bAdd = b as typeof a;
      if (a.children.length !== bAdd.children.length) return false;
      return a.children.every((child, i) => equals(child, bAdd.children[i]!));
    }
    case 'mul': {
      const bMul = b as typeof a;
      if (a.children.length !== bMul.children.length) return false;
      return a.children.every((child, i) => equals(child, bMul.children[i]!));
    }
    case 'pow': {
      const bPow = b as typeof a;
      return equals(a.base, bPow.base) && equals(a.exp, bPow.exp);
    }
    case 'func': {
      const bFunc = b as typeof a;
      if (a.name !== bFunc.name) return false;
      if (a.args.length !== bFunc.args.length) return false;
      return a.args.every((arg, i) => equals(arg, bFunc.args[i]!));
    }
    case 'sigma': {
      const bSigma = b as typeof a;
      return (
        a.index === bSigma.index &&
        equals(a.from, bSigma.from) &&
        equals(a.to, bSigma.to) &&
        equals(a.body, bSigma.body)
      );
    }
  }
}

export function structuralHash(e: Expr): string {
  switch (e.kind) {
    case 'num': {
      return `num(${e.value.toString()})`;
    }
    case 'var': {
      return `var(${e.name})`;
    }
    case 'add': {
      const childHashes = e.children.map((c) => structuralHash(c)).join('|');
      return `add(${childHashes})`;
    }
    case 'mul': {
      const childHashes = e.children.map((c) => structuralHash(c)).join('|');
      return `mul(${childHashes})`;
    }
    case 'pow': {
      const baseHash = structuralHash(e.base);
      const expHash = structuralHash(e.exp);
      return `pow(${baseHash},${expHash})`;
    }
    case 'func': {
      const argHashes = e.args.map((a) => structuralHash(a)).join('|');
      return `func(${e.name},${argHashes})`;
    }
    case 'sigma': {
      const fromHash = structuralHash(e.from);
      const toHash = structuralHash(e.to);
      const bodyHash = structuralHash(e.body);
      return `sigma(${e.index},${fromHash},${toHash},${bodyHash})`;
    }
  }
}

function sortKey(e: Expr): [number, string] {
  // Classification order:
  // 0 = num
  // 1 = var
  // 2 = pow
  // 3 = func
  // 4 = sigma
  // 5 = add
  // 6 = mul

  let classOrder: number;
  let hashBreaker: string;

  switch (e.kind) {
    case 'num':
      classOrder = 0;
      hashBreaker = e.value.toString();
      break;
    case 'var':
      classOrder = 1;
      hashBreaker = e.name;
      break;
    case 'pow':
      classOrder = 2;
      hashBreaker = structuralHash(e);
      break;
    case 'func':
      classOrder = 3;
      hashBreaker = structuralHash(e);
      break;
    case 'sigma':
      classOrder = 4;
      hashBreaker = structuralHash(e);
      break;
    case 'add':
      classOrder = 5;
      hashBreaker = structuralHash(e);
      break;
    case 'mul':
      classOrder = 6;
      hashBreaker = structuralHash(e);
      break;
  }

  return [classOrder, hashBreaker];
}

export function canonicalise(e: Expr): Expr {
  switch (e.kind) {
    case 'num':
      return e;

    case 'var':
      return e;

    case 'add': {
      // Recursively canonicalise children first
      const canonicalChildren = e.children.map((c) => canonicalise(c));

      // Flatten nested add
      const flattened: Expr[] = [];
      for (const child of canonicalChildren) {
        if (child.kind === 'add') {
          flattened.push(...child.children);
        } else {
          flattened.push(child);
        }
      }

      // Combine numeric children and drop zeros
      let numericSum = Fraction.ZERO;
      const nonNumeric: Expr[] = [];

      for (const child of flattened) {
        if (child.kind === 'num') {
          numericSum = numericSum.add(child.value);
        } else {
          nonNumeric.push(child);
        }
      }

      // Add the numeric sum if non-zero
      const combined: Expr[] = numericSum.num !== 0 ? [num(numericSum), ...nonNumeric] : nonNumeric;

      // Sort combined children by sortKey
      combined.sort(
        (a, b) => sortKey(a)[0] - sortKey(b)[0] || sortKey(a)[1].localeCompare(sortKey(b)[1]),
      );

      // Collapse to single child or identity
      if (combined.length === 0) return num(0);
      if (combined.length === 1) return combined[0]!;
      return add(...combined);
    }

    case 'mul': {
      // Recursively canonicalise children first
      const canonicalChildren = e.children.map((c) => canonicalise(c));

      // Flatten nested mul
      const flattened: Expr[] = [];
      for (const child of canonicalChildren) {
        if (child.kind === 'mul') {
          flattened.push(...child.children);
        } else {
          flattened.push(child);
        }
      }

      // Check for zero factor early
      for (const child of flattened) {
        if (child.kind === 'num' && child.value.num === 0) {
          return num(0);
        }
      }

      // Combine numeric children and drop ones
      let numericProd = Fraction.ONE;
      const nonNumeric: Expr[] = [];

      for (const child of flattened) {
        if (child.kind === 'num') {
          numericProd = numericProd.mul(child.value);
        } else {
          nonNumeric.push(child);
        }
      }

      // Add the numeric product if not one
      const combined: Expr[] = !numericProd.equals(Fraction.ONE)
        ? [num(numericProd), ...nonNumeric]
        : nonNumeric;

      // Sort combined children by sortKey
      combined.sort(
        (a, b) => sortKey(a)[0] - sortKey(b)[0] || sortKey(a)[1].localeCompare(sortKey(b)[1]),
      );

      // Collapse to single child or identity
      if (combined.length === 0) return num(1);
      if (combined.length === 1) return combined[0]!;
      return mul(...combined);
    }

    case 'pow': {
      const base = canonicalise(e.base);
      const exp = canonicalise(e.exp);
      return pow(base, exp);
    }

    case 'func': {
      const args = e.args.map((a) => canonicalise(a));
      return func(e.name, ...args);
    }

    case 'sigma': {
      const from = canonicalise(e.from);
      const to = canonicalise(e.to);
      const body = canonicalise(e.body);
      return sigma(e.index, from, to, body);
    }
  }
}

export function freeVars(e: Expr): ReadonlySet<string> {
  const vars = new Set<string>();

  function collect(node: Expr): void {
    switch (node.kind) {
      case 'num':
        break;
      case 'var':
        vars.add(node.name);
        break;
      case 'add':
        for (const child of node.children) {
          collect(child);
        }
        break;
      case 'mul':
        for (const child of node.children) {
          collect(child);
        }
        break;
      case 'pow':
        collect(node.base);
        collect(node.exp);
        break;
      case 'func':
        for (const arg of node.args) {
          collect(arg);
        }
        break;
      case 'sigma':
        collect(node.from);
        collect(node.to);
        collect(node.body);
        break;
    }
  }

  collect(e);
  return vars;
}

export function nodeAtPath(e: Expr, path: readonly number[]): Expr | null {
  let current = e;

  for (const index of path) {
    const children = getChildren(current);
    if (children === null || index < 0 || index >= children.length) {
      return null;
    }
    current = children[index]!;
  }

  return current;
}

function getChildren(e: Expr): Expr[] | null {
  switch (e.kind) {
    case 'add':
      return e.children;
    case 'mul':
      return e.children;
    case 'pow':
      return [e.base, e.exp];
    case 'func':
      return e.args;
    case 'sigma':
      return [e.from, e.to, e.body];
    case 'num':
    case 'var':
      return null;
  }
}

export function replaceAtPath(e: Expr, path: readonly number[], replacement: Expr): Expr {
  // Base case: empty path
  if (path.length === 0) {
    return replacement;
  }

  const firstIndex = path[0]!;
  const restPath = path.slice(1);

  const children = getChildren(e);
  if (children === null || firstIndex < 0 || firstIndex >= children.length) {
    // Out of range, return unchanged
    return e;
  }

  const newChild = replaceAtPath(children[firstIndex]!, restPath, replacement);

  // Create a new node with the updated child
  switch (e.kind) {
    case 'add': {
      const newChildren = [...e.children];
      newChildren[firstIndex] = newChild;
      return add(...newChildren);
    }
    case 'mul': {
      const newChildren = [...e.children];
      newChildren[firstIndex] = newChild;
      return mul(...newChildren);
    }
    case 'pow': {
      if (firstIndex === 0) {
        return pow(newChild, e.exp);
      } else if (firstIndex === 1) {
        return pow(e.base, newChild);
      }
      return e;
    }
    case 'func': {
      const newArgs = [...e.args];
      newArgs[firstIndex] = newChild;
      return func(e.name, ...newArgs);
    }
    case 'sigma': {
      if (firstIndex === 0) {
        return sigma(e.index, newChild, e.to, e.body);
      } else if (firstIndex === 1) {
        return sigma(e.index, e.from, newChild, e.body);
      } else if (firstIndex === 2) {
        return sigma(e.index, e.from, e.to, newChild);
      }
      return e;
    }
    case 'num':
    case 'var':
      return e;
  }
}
